import { split } from 'apollo-link';
import { ApolloClient } from 'apollo-client';
import { InMemoryCache, IntrospectionFragmentMatcher } from 'apollo-cache-inmemory';
import { HttpLink } from 'apollo-link-http';
import { onError } from 'apollo-link-error';
import { ApolloLink, Observable } from 'apollo-link';
import { getMainDefinition } from 'apollo-utilities';
import * as AbsintheSocket from '@absinthe/socket';
import { createAbsintheSocketLink } from '@absinthe/socket-apollo-link';
import { Socket as PhoenixSocket } from 'phoenix';

import i18n from 'i18next';

import { authHeaders, unsafeGetAccessToken, logout } from './auth';
import { notifyGlobalError, WebSocketAccessTokenError } from './errors';

import introspectionQueryResultData from '../fragmentTypes.json';

const fragmentMatcher = new IntrospectionFragmentMatcher({
  introspectionQueryResultData,
});

const findValidationErrors = errors => (errors.find(e => e.type === 'validation') || {}).details;

const request = async operation => {
  const headers = await authHeaders();
  operation.setContext({
    headers: {
      ...headers,
      'Accept-Language': i18n.languages[0],
    },
  });
};

const authLink = new ApolloLink(
  (operation, forward) =>
    new Observable(observer => {
      let handle;
      Promise.resolve(operation)
        .then(oper => request(oper))
        .then(() => {
          handle = forward(operation).subscribe({
            next: observer.next.bind(observer),
            error: observer.error.bind(observer),
            complete: observer.complete.bind(observer),
          });
        })
        .catch(observer.error.bind(observer));

      return () => {
        if (handle) handle.unsubscribe();
      };
    }),
);

// https://github.com/absinthe-graphql/absinthe-socket/issues/10#issuecomment-405524086
class WsLink extends ApolloLink {
  constructor(graphqlUrlWs, fallbackIframe) {
    super();
    const _getParams = token => {
      if (!token) throw new WebSocketAccessTokenError('Cannot find access token for websockets');
      return { params: { token } };
    };
    unsafeGetAccessToken({ fallbackIframe }).then(token => {
      if (token) {
        this.link = createAbsintheSocketLink(AbsintheSocket.create(new PhoenixSocket(graphqlUrlWs, _getParams(token))));
      }
    });
  }

  request(operation, forward) {
    if (this.link) {
      return this.link.request(operation, forward);
    }
  }
}

const initApolloClient = (graphqlUrl, graphqlUrlWs, fallbackIframe) => {
  const cache = new InMemoryCache({ fragmentMatcher });

  const httpLink = ApolloLink.from([
    onError(({ graphQLErrors, networkError }) => {
      const serverError = networkError || !findValidationErrors(graphQLErrors);
      if (networkError && networkError.statusCode === 401) {
        logout();
        window.location = '/';
      }

      if (serverError) notifyGlobalError();
    }),
    authLink,
    new HttpLink({
      uri: graphqlUrl,
    }),
  ]);

  const link = split(
    // split based on operation type
    ({ query }) => {
      const definition = getMainDefinition(query);
      return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
    },
    new WsLink(graphqlUrlWs, fallbackIframe),
    httpLink,
  );

  return new ApolloClient({ link, cache });
};

const withGraphQLErrors = async fn => {
  try {
    return await fn();
  } catch (res) {
    const errors = findValidationErrors(res.graphQLErrors);
    return { errors };
  }
};

export { withGraphQLErrors, initApolloClient };
